import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Whether an element is narrower than one or more pixel thresholds (MI-336).
 *
 * A viewport breakpoint answers "how big is the window", which stopped being
 * the question once the Movements page had a panel the reader can drag: the
 * list and the panel each get whatever the other is not using, so both have to
 * lay themselves out from their *own* width.
 *
 * Pass a number for one threshold, or an array for several — the answers come
 * back in order after the ref:
 *
 *   const [ref, isNarrow] = useNarrowerThan(380);
 *   const [ref, isNarrow, isTight] = useNarrowerThan([640, 900]);
 *
 * State is one boolean per threshold rather than the measured width, so
 * dragging the panel only re-renders the list when a threshold is actually
 * crossed — not on every frame of the drag.
 */
export function useNarrowerThan(threshold) {
  const thresholds = Array.isArray(threshold) ? threshold : [threshold];
  const ref = useRef(null);
  const [flags, setFlags] = useState(() => thresholds.map(() => false));

  // Serialized so an inline array literal at the call site doesn't restart the
  // observer on every render.
  const key = thresholds.join(',');

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const limits = key.split(',').map(Number);
    const apply = (width) => setFlags(prev => {
      const next = limits.map(limit => width < limit);
      return prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next;
    });
    // Measured once here, before the browser paints, because callers now
    // choose a layout from these flags rather than only trimming one: waiting
    // for the observer's first callback would paint a wide row on a narrow
    // screen and swap it a frame later.
    const style = window.getComputedStyle(node);
    apply(node.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight));
    const observer = new ResizeObserver(([entry]) => apply(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, [key]);

  return [ref, ...flags];
}
